import React from 'react';
import PropTypes from 'prop-types';
import { observable, action, autorun } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import GlobalEvent from '../../utils/globalEvent';
import './style.scss';

const HANDLE_SIZE = 16;
const HANDLE_HALF = HANDLE_SIZE / 2;
const ANIMTATION_TIME = 250;

class Handle {

	@observable value;
	@observable isSliding = false;

	x = 0;
	lastValidValue = 0;
	mouseOffset = 0;
	handle = null;

	constructor(value) {
		this.value = value;
	}

}

@observer
export default class SlideInput extends React.Component {

	static propTypes = {
		min: PropTypes.number,
		max: PropTypes.number,
		value: PropTypes.number,
		step: PropTypes.number,
		className: PropTypes.string,
		style: PropTypes.object,
		showInput: PropTypes.bool,
		onChange: PropTypes.func,
		onChangeEnd: PropTypes.func,
		onSlideStart: PropTypes.func,
		disabled: PropTypes.bool,
	};

	static defaultProps = {
		min: 0,
		max: 100,
		step: 1,
		showInput: false,
		disabled: false,
	};

	@observable isSliding = false;
	prevValue = 0;
	changed = false;

	offset = 0;
	width = 100;

	constructor(props) {
		super(props);
		this.props = props;
		this.range = props.max - props.min;
		const fracArr = this.props.step.toString().split('.');
		this.precision = fracArr.length > 1 ? fracArr[1].length : 0;
		// console.log('precision', this.precision);

		const value = props.value ? this.correctValue(props.value) : props.min;

		this.handle = new Handle(value);
	}

	numericValue = value => value.replace(/[^0-9\-.,]/, '').replace('\,', '.');
	correctValue = value => Math.max(this.props.min, Math.min(this.props.max, value));

	componentDidMount() {
		document.addEventListener('mouseup', this.onGlobalMouseUp);
		document.addEventListener('mousemove', this.onGlobalMouseMove);
		autorun(() => {
			this.placeHandle(undefined, false);
			this.updateRangeLine();
		}, 100);
	}
	
	componentWillUnmount() {
		document.removeEventListener('mouseup', this.onGlobalMouseUp);
		document.removeEventListener('mousemove', this.onGlobalMouseMove);
	}
	
	componentDidUpdate(prevProps, prevState, snapshot) {
		this.range = this.props.max - this.props.min;
		if (prevProps.value !== this.props.value && !this.isSliding) {
			this.handle.value = this.props.value;
			this.placeHandle(this.props.value, false);
			this.updateRangeLine();
		}
	}

	@action updateValue() {
		this.handle.value = this.props.value;
		this.placeHandle();
		this.updateRangeLine();
	}

	placeHandle = (value, animate = true) => {
		const rect = this.el.getBoundingClientRect();
		this.width = rect.width;
		const delta = this.props.min;
		const placeValue = value || this.handle.value;
		this.range = this.props.max - this.props.min;
		this.handle.x = (placeValue - delta) * this.width / this.range;
		// console.log('placeValue', placeValue, this.width, this.handle.value, this.range, this.handle.x);
		this.handle.handle.style.transition = animate ? `transform ${ANIMTATION_TIME}ms` : 'none';
		this.handle.handle.style.transform = `translateX(${this.handle.x}px)`;
	};
	
	updateRangeLine = () => {
		const scale = this.handle.x / this.width;
		this.rangeLine.style.transition = `transform ${ANIMTATION_TIME}ms`;
		this.rangeLine.style.transform = `scaleX(${scale})`;
	};

	onGlobalMouseUp = () => {
		if (this.isSliding) {
			this.stopSliding();
			this.placeHandle();
			this.updateRangeLine();
			if (this.props.onChangeEnd) this.props.onChangeEnd(parseFloat(this.handle.value));
		}
	};

	onGlobalMouseMove = e => {
		if (this.isSliding) {
			const rect = this.el.getBoundingClientRect();
			this.width = rect.width;
			this.offset = rect.left;
			this.scale = this.range / this.width;
			this.percentWidth = 100 / this.width;
			const x = e.clientX - this.offset;
			// console.log('move', x, this.offset);
			if (x >= -HANDLE_HALF && x <= this.width + HANDLE_HALF) {
				this.onDrag(x);
			}
		}
	};

	onSliderMount = el => {
		this.el = el;
		if (el) {
			const rect = el.getBoundingClientRect();
			// console.log('rect', rect);
			this.width = rect.width;
			this.scale = this.range / this.width;
			this.offset = rect.left;
			this.percentWidth = 100 / this.width;
		}
	};

	onInputFocus = () => {
		this.handle.lastValidValue = this.handle.value;
		// console.log('lastValidValue', this.handle.lastValidValue);
		this.input.select();
	};

	@action
	onInputChange = e => {
		const newValue = parseFloat(this.numericValue(e.target.value));
		this.handle.value = newValue;
		
		if (!isNaN(newValue) && newValue >= this.props.min && newValue <= this.props.max) {
			// console.log('ok, newValue', newValue, this.handle.lastValidValue);
			this.handle.lastValidValue = this.correctValue(newValue);
			this.placeHandle(this.handle.lastValidValue);
			this.updateRangeLine();
			this.changed = true;
		}
	};

	@action
	onInputBlur = () => {
		this.handle.value = this.handle.lastValidValue;
		this.commitChanges();
	};

	@action
	commitChanges = () => {
		if (this.changed && this.props.onChange) {
			this.props.onChange(parseFloat(this.handle.value));
		}
		this.changed = false;
	};

	@action
	startSliding = e => {
		this.handle.isSliding = true;
		this.handle.mouseOffset = e.clientX - e.target.getBoundingClientRect().left - HANDLE_HALF;
		this.handle.handle.style.zIndex = 2;
		document.body.classList.add('noselect');
		this.isSliding = true;
		if (this.props.onSlideStart) this.props.onSlideStart(parseFloat(this.handle.value));
	};

	@action
	stopSliding = () => {
		this.isSliding = false;
		this.handle.isSliding = false;
		document.body.classList.remove('noselect');
		// this.commitChanges();
	};

	constraints = newX => {
		newX = Math.max(0, newX);
		newX = Math.min(this.width, newX);
		return newX;
	};

	@action
	onDrag = mouseX => {
		const x = this.constraints(mouseX - this.handle.mouseOffset);
		if (x >= 0 && x < this.width) {
			// this.handle.handle.style.left = x - HANDLE_HALF + 'px';
			// this.rangeLine.style.width = x + 'px';
			this.handle.handle.style.transition = `none`;
			this.handle.handle.style.transform = `translateX(${x}px)`;
			const scale = x / this.width;
			this.rangeLine.style.transition = `none`;
			this.rangeLine.style.transform = `scaleX(${scale})`;
		}

		const rawValue = x * this.scale + this.props.min;
		let newValue = Math.round(rawValue / this.props.step) * this.props.step;

		const maxValid = this.props.max;
		if (newValue > maxValid) {
			if (this.handle.value === newValue.toFixed(this.precision)) {
				return;
			}
			newValue = maxValid;
		}

		this.handle.value = newValue.toFixed(this.precision);

		if (newValue !== this.prevValue) {
			this.changed = true;
			this.prevValue = newValue;
			this.commitChanges();
		}
	};

	@action onRangeClick = (e) => {
		const rect = this.el.getBoundingClientRect();
		this.width = rect.width;
		this.offset = rect.left;
		this.scale = this.range / this.width;
		this.percentWidth = 100 / this.width;
		const x = e.clientX - this.offset;
		// console.log('onRangeClick', x);
		this.onDrag(x);

		this.handle.isSliding = true;
		this.isSliding = true;
		if (this.props.onSlideStart) this.props.onSlideStart(parseFloat(this.handle.value));
	};

	render() {
		const { className, showInput, disabled, style } = this.props;

		const _className = classNames('sp-slide-input', className, { disabled });

		return <div className={_className} style={style}>
			<div className="sp-slide-input-inner">
				<div className={'sp-slider ' + (this.isSliding ? 'active' : '')}>
					<div className="sp-line" ref={this.onSliderMount} onMouseDown={this.onRangeClick} />
					<div className="sp-range-line" ref={el => this.rangeLine = el} />
					<div className={'sp-handle' + (this.handle.isSliding ? ' active' : '')}
					     ref={el => this.handle.handle = el}
					     onMouseDown={e => this.startSliding(e)}
					     onDragStart={e => e.preventDefault()} />
				</div>
				
				{showInput &&
					<input
						type="text"
						className="sp-value-input"
						value={this.handle.value}
						ref={el => this.input = el}
						onChange={this.onInputChange}
						onFocus={this.onInputFocus}
						onBlur={this.onInputBlur} />
				}
			</div>
		</div>;
	}

}
